import { useRef, useState } from "react";
import { ShieldCheck, Lock, Mail, Copy, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/App";
import { ForgotPasswordStep } from "@/components/ForgotPasswordStep";
import { EmailOtpButton } from "@/components/EmailOtpButton";

function CredentialsStep({ onNext, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onNext(data);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="login-form">
      <Field icon={Mail} label="Email">
        <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="nome@trustmaster.it" className="pl-10 h-11 bg-slate-900/70 border-slate-700 text-slate-100" />
      </Field>
      <Field icon={Lock} label="Password">
        <Input data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••" className="pl-10 h-11 bg-slate-900/70 border-slate-700 text-slate-100" />
      </Field>
      <div className="flex justify-end -mt-2">
        <button type="button" onClick={onForgot} data-testid="forgot-password-link" className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
          Password dimenticata?
        </button>
      </div>
      {error && <p className="text-sm text-rose-400" data-testid="login-error">{error}</p>}
      <Button data-testid="login-submit-button" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium">
        {loading ? "Verifica…" : "Continua"}
      </Button>
    </form>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <label className="block space-y-2">
      <span className="text-xs uppercase tracking-[0.18em] text-slate-400 font-mono">{label}</span>
      <div className="relative">
        <Icon className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
        {children}
      </div>
    </label>
  );
}

function OtpStep({ mfa, onBack }) {
  const { setUser } = useAuth();
  const [code, setCode] = useState("");
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const verify = async (value) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-otp", { mfa_token: mfa.mfa_token, code: value });
      toast.success("Codice OTP verificato");
      setUser(data);
    } catch (err) {
      setError(errMsg(err));
      setCode("");
      setTimeout(() => inputRef.current?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(mfa.secret);
    toast.success("Chiave segreta copiata");
  };

  return (
    <div className="space-y-6" data-testid="totp-step">
      {mfa.setup_required && (
        <div className="space-y-4" data-testid="totp-setup">
          <p className="text-sm text-slate-300 leading-relaxed">
            Primo accesso: scansiona il QR con <span className="text-slate-100">Google Authenticator</span> o <span className="text-slate-100">Authy</span>, poi inserisci il codice a 6 cifre.
          </p>
          <div className="flex gap-4 items-center">
            <img src={mfa.qr} alt="QR code OTP" data-testid="totp-qr-code" className="w-36 h-36 rounded-lg bg-white p-2 shrink-0" />
            <div className="min-w-0 space-y-2">
              <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-mono">Chiave manuale</span>
              <p className="font-mono text-xs text-slate-300 break-all" data-testid="totp-secret">{mfa.secret}</p>
              <Button type="button" variant="outline" size="sm" onClick={copySecret} data-testid="totp-copy-secret-button"
                className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copia
              </Button>
            </div>
          </div>
        </div>
      )}
      {!mfa.setup_required && (
        <p className="text-sm text-slate-400 leading-relaxed" data-testid="totp-hint">
          Apri l'app <span className="text-slate-200">Google Authenticator</span> o <span className="text-slate-200">Authy</span> sul telefono e inserisci il codice a 6 cifre di TrustMaster, oppure ricevilo via email.
        </p>
      )}
      <div className="space-y-3">
        <span className="text-xs uppercase tracking-[0.18em] text-slate-400 font-mono flex items-center gap-2">
          <KeyRound className="w-3.5 h-3.5" /> Codice OTP
        </span>
        <InputOTP ref={inputRef} maxLength={6} value={code} onChange={setCode} onComplete={verify} disabled={loading} data-testid="totp-input-code" autoFocus>
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot key={i} index={i} className="w-12 h-12 text-lg font-mono border-slate-700 bg-slate-900/70 text-slate-100" />
            ))}
          </InputOTPGroup>
        </InputOTP>
        <EmailOtpButton mfaToken={mfa.mfa_token} />
      </div>
      {error && <p className="text-sm text-rose-400" data-testid="totp-error">{error}</p>}
      <div className="flex gap-3">
        <Button type="button" variant="ghost" onClick={onBack} data-testid="totp-back-button" className="text-slate-400 hover:text-white hover:bg-slate-800">
          <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
        </Button>
        <Button onClick={() => verify(code)} disabled={loading || code.length !== 6} data-testid="totp-verify-button"
          className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white">
          {loading ? "Verifica…" : "Verifica e accedi"}
        </Button>
      </div>
    </div>
  );
}

export default function Login() {
  const [mfa, setMfa] = useState(null);
  const [forgot, setForgot] = useState(false);

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] grid-bg noise">
      <aside className="hidden lg:flex flex-col justify-between p-14 border-r border-slate-800/80">
        <div className="flex items-center gap-2 text-slate-300 font-mono text-xs tracking-[0.2em] uppercase">
          <ShieldCheck className="w-4 h-4 text-emerald-400" /> TrustMaster
        </div>
        <div className="rise max-w-md">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.02]">
            Trust Master.<br /><span className="text-blue-500">Un solo accesso.</span>
          </h1>
          <p className="mt-6 text-slate-400 text-base leading-relaxed">
            Il tuo hub privato protetto da password e codice OTP a due fattori.
          </p>
        </div>
        <p className="font-mono text-[11px] text-slate-600">Accesso riservato · 2FA TOTP</p>
      </aside>
      <main className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm rise" style={{ animationDelay: "120ms" }}>
          <div className="lg:hidden flex items-center gap-2 mb-10 text-slate-300 font-mono text-xs tracking-[0.2em] uppercase">
            <ShieldCheck className="w-4 h-4 text-emerald-400" /> TrustMaster
          </div>
          <span className="font-mono text-[11px] text-blue-400 tracking-[0.2em] uppercase" data-testid="login-step-indicator">
            {forgot ? "Recupero accesso" : `Passo ${mfa ? "2" : "1"} / 2`}
          </span>
          <h2 className="font-display text-3xl font-bold mt-2 mb-8" data-testid="login-title">
            {forgot ? "Password dimenticata" : mfa ? "Verifica a due fattori" : "Accedi"}
          </h2>
          {forgot ? <ForgotPasswordStep onBack={() => setForgot(false)} />
            : mfa ? <OtpStep mfa={mfa} onBack={() => setMfa(null)} />
            : <CredentialsStep onNext={setMfa} onForgot={() => setForgot(true)} />}
        </div>
      </main>
    </div>
  );
}
